import React, { useState } from 'react';
import styled from 'styled-components';

interface ImageCarouselProps {
  images: { src: string; alt: string }[];
  onImageClick: (src: string, alt: string) => void;
}

const CarouselContainer = styled.div`
  position: relative;
  background: linear-gradient(135deg, rgba(99, 179, 237, 0.1), rgba(144, 205, 244, 0.05));
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid rgba(66, 153, 225, 0.2);
  box-shadow: 0 8px 25px rgba(66, 153, 225, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 35px rgba(66, 153, 225, 0.15);
  }

  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: 300px;
  overflow: hidden;

  @media (max-width: 1024px) {
    height: 250px;
  }

  @media (max-width: 768px) {
    height: 200px;
  }
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const NavigationButton = styled.button<{ direction: 'prev' | 'next' }>`
  position: absolute;
  top: 50%;
  ${props => props.direction === 'prev' ? 'left: 1rem' : 'right: 1rem'};
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  color: #3182ce;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.2);
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 6px 20px rgba(66, 153, 225, 0.3);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: translateY(-50%);
  }

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 1rem;
    ${props => props.direction === 'prev' ? 'left: 0.5rem' : 'right: 0.5rem'};
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
`;

const Dot = styled.button<{ isActive: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.isActive ? '#3182ce' : 'rgba(66, 153, 225, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.isActive ? '#2c5282' : 'rgba(66, 153, 225, 0.5)'};
    transform: scale(1.2);
  }

  @media (max-width: 768px) {
    width: 10px;
    height: 10px;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  z-index: 2;

  @media (max-width: 768px) {
    top: 0.5rem;
    right: 0.5rem;
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
`;

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };

  const goToNext = () => {
    setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  if (!images || images.length === 0) {
    return (
      <CarouselContainer>
        <ImageContainer>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#718096',
            fontSize: '1.1rem',
            textAlign: 'center'
          }}>
            📸 No images available
          </div>
        </ImageContainer>
      </CarouselContainer>
    );
  }

  const currentImage = images[currentIndex];

  return (
    <CarouselContainer>
      <ImageContainer>
        <CarouselImage
          src={currentImage.src}
          alt={currentImage.alt}
          onClick={() => onImageClick(currentImage.src, currentImage.alt)}
        />
        
        {images.length > 1 && (
          <>
            <NavigationButton
              direction="prev"
              onClick={goToPrevious}
              disabled={images.length <= 1}
            >
              ‹
            </NavigationButton>
            
            <NavigationButton
              direction="next"
              onClick={goToNext}
              disabled={images.length <= 1}
            >
              ›
            </NavigationButton>
            
            <ImageCounter>
              {currentIndex + 1} / {images.length}
            </ImageCounter>
          </>
        )}
      </ImageContainer>
      
      {images.length > 1 && (
        <DotsContainer>
          {images.map((_, index) => (
            <Dot
              key={index}
              isActive={index === currentIndex}
              onClick={() => goToSlide(index)}
            />
          ))}
        </DotsContainer>
      )}
    </CarouselContainer>
  );
};

export default ImageCarousel; 